// Ported from dev-scrips-pm-react src/billing/components/payment-receipt-card.tsx (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { format as formatDateFns, isValid as isDateValid, parseISO } from 'date-fns';
import { personRefName, type PaymentRow } from '@/shared/types/billing';

/**
 * One recorded payment, read-only — Flutter `InvoicePaymentWidget.paymentItem`
 * (`invoice_payment_widget.dart:170-457`). Three columns of label/value facts
 * (paid + voided variants), a divider, then a caller-supplied actions row.
 *
 * Flutter renders this SAME card on both surfaces; only the actions differ by
 * `isClaimWorkbench`:
 *   - Claim popup (Review Claim):      Print + View Receipt
 *   - Appointment / patient billing:   Void Payment + View Receipt (+ Print/Refund in React today)
 * So the card is presentation-only and takes an `actions` slot — the host wires
 * the surface-appropriate buttons (use `ReceiptActionButton` for the circular
 * icon-tile styling shared across surfaces).
 *
 * PROD-1001 (claim) / PROD-1002 (appointment billing reuse).
 */
const PAYMENT_STATUS_VOIDED = 'Voided';

interface Props {
  payment: PaymentRow;
  /** ISO-4217 code, e.g. "AED". */
  currency: string;
  /** Surface-specific action tiles (Print / View Receipt / Void / Refund). */
  actions?: React.ReactNode;
}

export function PaymentReceiptCard({ payment, currency, actions }: Props) {
  const isVoided = (payment.status ?? '') === PAYMENT_STATUS_VOIDED;
  const money = (n?: number | null) => (n == null ? '—' : `${currency} ${n}`);

  return (
    <div className="rounded-xl border border-border-default bg-surface-card overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1 px-6 py-6">
        {/* Column 1 */}
        <div className="flex flex-col gap-1">
          <Row label="Payment method">{payment.paymentMethod ?? '—'}</Row>
          {isVoided ? (
            <Row label="Voided date">{fmtDateTime(payment.voidedDate) ?? '—'}</Row>
          ) : (
            <Row label="Payment date">{fmtDateTime(payment.paymentDate) ?? '—'}</Row>
          )}
          <Row label="Encounter date">{fmtDateTime(payment.encounterDate) ?? '—'}</Row>
          <Row label="Receipt #">{payment.receiptNumber ?? '—'}</Row>
        </div>

        {/* Column 2 */}
        <div className="flex flex-col gap-1">
          <Row label="To invoice #">{payment.invoiceNumber ?? '—'}</Row>
          <Row label="Invoice date">{fmtDateTime(payment.invoiceDate) ?? '—'}</Row>
          <Row label="Provider">{payment.provider?.name ?? '—'}</Row>
          {/* Wire typo `recieptAmount` preserved — see PaymentRow type comment. */}
          <Row label="Receipt amount">{money(payment.recieptAmount)}</Row>
        </div>

        {/* Column 3 */}
        <div className="flex flex-col gap-1">
          <Row label="Status">{payment.status ?? '—'}</Row>
          {isVoided ? (
            <>
              <Row label="Amount voided">{money(payment.amount)}</Row>
              <Row label="Voided by">{personRefName(payment.voidedBy) ?? '—'}</Row>
              <Row label="Void reason">{payment.voidReason?.value ?? '—'}</Row>
              <Row label="Paid to">{payment.patient?.name ?? '—'}</Row>
            </>
          ) : (
            <>
              <Row label="Amount paid">{money(payment.amount)}</Row>
              <Row label="Received by">{personRefName(payment.receivedBy) ?? '—'}</Row>
              <Row label="Paid by">{payment.patient?.name ?? '—'}</Row>
            </>
          )}
        </div>
      </div>

      {actions ? (
        <div className="border-t border-border-default px-6 py-4 flex items-center justify-end gap-6">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

/** Circular icon-tile action button used in the receipt-card footer. */
export function ReceiptActionButton({
  icon,
  label,
  onClick,
  disabled,
  tone = 'primary',
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'primary' | 'error';
}) {
  const ring =
    tone === 'error'
      ? 'border-red-200 text-status-error'
      : 'border-blue-100 text-brand-primary';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-1 text-xs leading-5 text-text-secondary hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span
        className={`inline-flex h-10 w-10 items-center justify-center rounded-full bg-grey-100 border ${ring}`}
      >
        {icon}
      </span>
      <span className="text-center leading-tight">{label}</span>
    </button>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="text-xs font-medium leading-4 text-text-secondary shrink-0 w-28 text-right">
        {label}
      </div>
      <div className="text-sm leading-5 text-text-primary truncate">{children}</div>
    </div>
  );
}

/** "Jun 02, 2026 8:59p" — Flutter `scDateTimeFormat` parity (matches info-grid). */
function fmtDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = parseISO(value);
  if (!isDateValid(parsed)) return value;
  return formatDateFns(parsed, 'MMM d, yyyy h:mma')
    .replace(/AM$/, 'a')
    .replace(/PM$/, 'p');
}
